import {
	arrayItemSchema,
	dataAtPath,
	dateInputValue,
	enumOptions,
	FormRuntime,
	isRecord,
	JsonSchemaDefinition,
	resolveSchema,
	schemaType
} from "../../../../../../extensions/json-schema/runtime";
import {
	formTimeZone,
	FormTimeSettings
} from "../../../../../../extensions/json-schema/components/time-zone-context";

interface DisplayEntry {
	key: string;
	path: string;
	title: string;
	description?: string;
	schema: JsonSchemaDefinition;
	value: unknown;
}

export interface EntitySchemaColumn {
	key: string;
	title: string;
	schema: JsonSchemaDefinition;
}

export interface EntitySchemaDisplay {
	fields: Array<DisplayEntry & { displayValue: string | number }>;
	objects: Array<DisplayEntry & { isEmpty: boolean }>;
	tables: Array<DisplayEntry & {
		columns: EntitySchemaColumn[];
		rows: unknown[];
	}>;
	hasContent: boolean;
}

export function hasEntitySchemaValue(value: unknown): boolean {
	if(value === undefined || value === null || value === "") {
		return false;
	}

	if(Array.isArray(value)) {
		return value.some(hasEntitySchemaValue);
	}

	return isRecord(value) ? Object.values(value).some(hasEntitySchemaValue) : true;
}

export function formatEntitySchemaValue(
	value: unknown,
	schema?: JsonSchemaDefinition,
	form?: FormTimeSettings
): string | number {
	if(value === undefined || value === null || value === "") {
		return "--";
	}

	const option = schema && enumOptions(schema).find(candidate => candidate.value === value);

	if(option) {
		return option.label;
	}

	if(schema?.format === "file" || schema?.format === "base64") {
		return "File selected";
	}

	if(schema?.format === "date" || schema?.format === "date-time") {
		const mode = schema.metadata?.timeZoneMode ?? form?.TimeMode?.Code;

		return dateInputValue(value, schema, formTimeZone(schema, form), mode === "BYENTITY") || "--";
	}

	if(typeof value === "boolean") {
		return value ? "Yes" : "No";
	}

	if(Array.isArray(value)) {
		return value.length > 0 ? value.map(item => formatEntitySchemaValue(item)).join(", ") : "--";
	}

	if(isRecord(value)) {
		return JSON.stringify(value);
	}

	return typeof value === "number" ? value : String(value);
}

function schemaAtPath(root: JsonSchemaDefinition, path: string): JsonSchemaDefinition {
	return path.split(".").filter(Boolean)
		.reduce((schema, key) => schemaType(schema) === "array"
			? arrayItemSchema(root, schema, Number(key))
			: resolveSchema(root, schema.properties?.[key] ?? {}), resolveSchema(root, root));
}

export function buildEntitySchemaDisplay(
	schema: JsonSchemaDefinition,
	model: unknown,
	path = "",
	form?: FormTimeSettings
): EntitySchemaDisplay {
	const runtime = new FormRuntime(schema, model);
	const context = schemaAtPath(schema, path);

	const display: EntitySchemaDisplay = {
		fields: [],
		objects: [],
		tables: [],
		hasContent: false
	};

	const properties = schemaType(context) === "array"
		? [["", context] as const]
		: Object.entries(context.properties ?? {});

	for(const [key, property] of properties) {
		const fieldPath = key ? (path ? `${path}.${key}` : key) : path;

		if(!runtime.isVisible(fieldPath)) {
			continue;
		}

		const propertySchema = resolveSchema(schema, property);
		const value = dataAtPath(runtime.data, fieldPath);
		const entry: DisplayEntry = {
			key,
			path: fieldPath,
			title: propertySchema.title || key || "entries",
			description: propertySchema.description,
			schema: propertySchema,
			value
		};
		const type = schemaType(propertySchema);

		if(type === "object") {
			display.objects.push({
				...entry,
				isEmpty: !hasEntitySchemaValue(value)
			});
		} else if(type === "array") {
			const item = arrayItemSchema(schema, propertySchema, 0);
			const columns = schemaType(item) === "object"
				? Object.entries(item.properties ?? {}).map(([columnKey, column]) => {
					const columnSchema = resolveSchema(schema, column);

					return {
						key: columnKey,
						title: columnSchema.title || columnKey,
						schema: columnSchema
					};
				})
				: [{
					key: "",
					title: item.title || "Value",
					schema: item
				}];

			display.tables.push({
				...entry,
				columns,
				rows: Array.isArray(value) ? value : []
			});
		} else {
			display.fields.push({
				...entry,
				displayValue: formatEntitySchemaValue(value, propertySchema, form)
			});
		}
	}

	display.hasContent = display.fields.length + display.objects.length + display.tables.length > 0;

	return display;
}

export function formatEntitySchemaTableCell(row: unknown, column: EntitySchemaColumn, form?: FormTimeSettings): string | number {
	const value = column.key ? (isRecord(row) ? row[column.key] : undefined) : row;

	return formatEntitySchemaValue(value, column.schema, form);
}
